import React, { FC, Suspense, useCallback } from "react";
import { QueryClient, QueryClientProvider } from "react-query" ;
import { MutableSnapshot, RecoilRoot } from "recoil" ;
import { EcoInitialState } from "types";
import { EcoSpinner } from "components";
import Shell from "./Shell";
import { BrowserRouter, HashRouter } from "react-router-dom";
import { createTheme, StyledEngineProvider, ThemeOptions, ThemeProvider } from "@mui/material";
import { routesAtoms } from "index";
import { RouteConfig } from "@eco/client";
import smoothscroll from "smoothscroll-polyfill";
import { DefaultTheme } from "theme";


// Fix Edge unsuported scrollBy fn
smoothscroll.polyfill();


/**
 * 
 * Main application root component initializing shared resources :
 * 
 * - Global initial state (recoil) as provided by host App
 * - React query client
 * 
 */
const EcoRoot:FC<EcoRootType> = ( {initialState, queryClient, defaultRoute, theme, routes, routerType, spinner}: EcoRootType ) => {

    // initialize reactQuery
    const reactQueryClient = ensureReactQuery(queryClient) ;

    // initialize global recoil state
    const initializeState: (mutableSnapshot: MutableSnapshot) => void = ({set}) => {
        initialState?.forEach((entry) => {
            set(entry.state, entry.value) ;
        });

        routes && set(routesAtoms, routes)
    }

    const getRouterType = useCallback((children: React.ReactElement) => {

        if (routerType === 'hash') {
            return (
                <HashRouter>
                    { children }
                </HashRouter>
            )
        }
        
        return (
            <BrowserRouter>
                { children }
            </BrowserRouter>
        )
    }, [])

    return (
        <RecoilRoot initializeState={initializeState}>
            <QueryClientProvider client={reactQueryClient}>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={createTheme({ ...DefaultTheme, ...theme})}>
                        <Suspense fallback={<div id="loading">{ spinner || <EcoSpinner /> }</div>}>
                            { getRouterType(<Shell defaultRoute={defaultRoute} />) }
                        </Suspense>
                    </ThemeProvider>
                </StyledEngineProvider>
            </QueryClientProvider>
        </RecoilRoot>
    );
}

const ensureReactQuery = (queryClient:QueryClient|undefined) => {
    return queryClient ? queryClient : new QueryClient({
        defaultOptions: { 
            queries:  {
                refetchOnWindowFocus: false,
                refetchOnMount: false,
                retry:2
            }
        }
    }) ;
}


export type EcoRootType = {
    /**
     * Component that will showed as loading when app is loading
     */
    spinner?: React.ReactElement
    /**
     * Array of recoil state to be initialized at app bootstrap
     */
    initialState?: EcoInitialState[]
    
    /**
     * Custom react query client  
     */
    queryClient?: QueryClient

    /**
     * Use it if you want to load route json directly.
     * The ecoClient's route fetching is still avalaible
     */
    routes?: RouteConfig[]

    /**
     * Default route if route is equals to "/"
     */
    defaultRoute?: string
    
    /**
     * Router type should be used by react-router
     * @default browser
     */
    routerType?: 'browser' | 'hash'
    /** 
     * Material UI Theme, it's provide a theme to "react-lib"'s material-ui Theme
     * It's only override theme of react-lib's material-ui component 
     * So you need to use ThemeProvider wrapper inside your application to change your application theme
     * Just give the same Theme object so bouth theme will sync
     */
    theme?: ThemeOptions

}

export default EcoRoot ;
